import { Participant } from '@app/api';
import { PARTICIPANT_ENTITY_NAME, participantSchema } from './definitions';
import { create, deleteOne, getList, update } from 'store/modules/fetch';
import { schema } from 'normalizr';
import { fetchWithAuth } from 'store/modules/Auth';
import { createApiException } from 'store/modules/exceptions';
import { API_URL } from '@app/api';
import { Devices } from './enums';

export type ParticipantAction =
  | {
      type: 'FETCH_FAST_PARTICIPATION_SUCCESS';
      payload: {
        ids: string[];
        panelId: string;
      };
      meta: any;
    }
  | {
      type: 'DELETE_PARTICIPANTS';
      payload: { participantIds: string[]; panelId: string };
    }
  | {
      type: 'NEW_PARTICIPANT';
      payload: Participant;
    };

export const parsePostgresArray = <T = string>(rawValue: string): Array<T> => {
  if (!rawValue) {
    return [];
  }
  const regRes = /^\{?(?<content>([a-zA-Z0-9_]+,?)*)\}?$/.exec(rawValue);
  if (!regRes || !regRes.groups || !regRes.groups.content) {
    return [];
  }

  return regRes.groups.content.split(',') as unknown[] as T[];
};

const createFetchFastParticipantSuccessAction = (
  panelId: string,
  participants: Participant[],
): ParticipantAction => {
  const ids: string[] = [];
  const normalized = {} as { [id: string]: Participant };
  participants.forEach((participant: Participant) => {
    ids.push(participant.id);
    if (participant.rawStudySubjectPreferences) {
      participant.studySubjectPreferences = parsePostgresArray(
        participant.rawStudySubjectPreferences,
      );
    }
    if (participant.rawStudyTypePreferences) {
      participant.studyTypePreferences = parsePostgresArray(
        participant.rawStudyTypePreferences,
      );
    }
    if (participant.rawIndustries) {
      participant.industries = parsePostgresArray(participant.rawIndustries);
    }
    if (participant.devices) {
      participant.devices = participant.devices as Devices[];
    }
    if (participant.tagIds) {
      // @ts-ignore
      participant.tags = participant.tagIds.split(',');
    }
    if (participant.countryKey && participant.countryName) {
      // @ts-ignore
      participant.country = participant.countryKey;
    }
    normalized[participant.id] = participant;
  });

  return {
    type: 'FETCH_FAST_PARTICIPATION_SUCCESS',
    payload: { ids, panelId },
    meta: {
      entities: {
        [PARTICIPANT_ENTITY_NAME]: normalized,
      },
    },
  };
};

const createNewParticipantAction = (
  participant: Participant,
): ParticipantAction => ({
  type: 'NEW_PARTICIPANT',
  payload: participant,
});

export const getParticipantsForPanel = (panelId: string) =>
  getList<Participant>({
    entity: PARTICIPANT_ENTITY_NAME,
    path: `/panels/${panelId}/participants`,
    schema: new schema.Array(participantSchema),
  });

export const getFastParticipantsForPanel = (panelId: string) => {
  return async (dispatch: Function): Promise<Participant[]> => {
    const url = `${API_URL}/panels/${panelId}/participants/fast`;

    try {
      const { data } = await dispatch(fetchWithAuth({ url }));
      dispatch(createFetchFastParticipantSuccessAction(panelId, data));
      return data;
    } catch (e) {
      throw createApiException(e as any);
    }
  };
};

export const createParticipantsInPanel =
  (participant: Partial<Participant>, panelId: string) =>
  async (dispatch: Function) => {
    const newParticipant = await dispatch(
      create<Participant>({
        entity: PARTICIPANT_ENTITY_NAME,
        path: `/panels/${panelId}/participants`,
        payload: participant,
        schema: participantSchema,
      }),
    );
    dispatch(createNewParticipantAction(newParticipant));
    return newParticipant;
  };

export const createParticipant = (participant: Partial<Participant>) =>
  create<Participant>({
    entity: PARTICIPANT_ENTITY_NAME,
    payload: participant,
    schema: participantSchema,
  });

export const updateParticipant = (
  participantId: string,
  participant: Partial<Participant>,
) =>
  update<Participant>({
    entity: PARTICIPANT_ENTITY_NAME,
    payload: participant,
    id: participantId,
    schema: participantSchema,
  });

export const deleteParticipant = (participantId: string) =>
  deleteOne<Participant>({
    entity: PARTICIPANT_ENTITY_NAME,
    id: participantId,
    schema: participantSchema,
  });

export const deleteParticipants = (
  panelId: string,
  participantIds: string[],
) => ({
  type: 'DELETE_PARTICIPANTS',
  payload: { panelId, participantIds },
  meta: {
    entities: {
      [PARTICIPANT_ENTITY_NAME]: participantIds.reduce((acc, id) => {
        acc[id] = {
          id,
          deletedAt: new Date().toISOString(),
        };
        return acc;
      }, {} as { [id: string]: any }),
    },
  },
});
